import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.jsx'

export default function TranscodingStatusPage() {
  const { user, tokens } = useAuth()
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => {
    fetchStatus()
  }, [])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(fetchStatus, 5000) // Refresh every 5 seconds
    return () => clearInterval(interval)
  }, [autoRefresh])

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/admin/transcoding/status', {
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) setStatus(data.data)
    } catch (e) {
      console.error('Failed to fetch status:', e)
    } finally {
      setLoading(false)
    }
  }

  if (!user || user.role !== 'superadmin') {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <div><h2>Access denied</h2><p>Superadmin required.</p></div>
      </div>
    )
  }

  if (loading) {
    return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><p>Loading...</p></div>
  }

  const formatTime = (date) => {
    return new Date(date).toLocaleString()
  }

  const deleteVideo = async (videoId) => {
    if (!confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      return
    }

    setDeleting(videoId)
    try {
      const res = await fetch(`/api/admin/videos/${videoId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        // Refresh the status to remove the deleted video
        await fetchStatus()
      } else {
        alert('Failed to delete video: ' + (data.message || 'Unknown error'))
      }
    } catch (e) {
      console.error('Failed to delete video:', e)
      alert('Failed to delete video')
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div style={{ maxWidth: 1200, margin: '32px auto', padding: '0 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 600 }}>Transcoding Status</h1>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={autoRefresh} 
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span style={{ fontSize: 14 }}>Auto-refresh (5s)</span>
        </label>
      </div>

      {/* Currently Processing */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          Currently Processing
          {status?.processingCount > 0 && (
            <span style={{ 
              background: '#f59e0b', 
              color: '#fff', 
              padding: '2px 8px', 
              borderRadius: 12, 
              fontSize: 12, 
              fontWeight: 600 
            }}>
              {status.processingCount}
            </span>
          )}
        </h2>
        {status?.processing?.length === 0 ? (
          <div style={{ padding: 16, background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', borderRadius: 12, fontSize: 14, color: 'var(--muted)' }}>
            No videos currently processing
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {status?.processing?.map(video => (
              <div key={video.id} style={{ 
                padding: 16, 
                background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', 
                borderRadius: 12,
                border: '1px solid #f59e0b33'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{video.title}</h3>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      Started: {formatTime(video.createdAt)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '4px 12px', 
                    background: '#f59e0b', 
                    color: '#fff', 
                    borderRadius: 6, 
                    fontSize: 12, 
                    fontWeight: 600 
                  }}>
                    {video.elapsedMinutes} min elapsed
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace' }}>
                  ID: {video.id}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recently Completed */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Recently Completed</h2>
        {status?.recentlyCompleted?.length === 0 ? (
          <div style={{ padding: 16, background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', borderRadius: 12, fontSize: 14, color: 'var(--muted)' }}>
            No completed videos yet
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {status?.recentlyCompleted?.map(video => (
              <div key={video.id} style={{ 
                padding: 16, 
                background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', 
                borderRadius: 12,
                border: '1px solid #16a34a33'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{video.title}</h3>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      Completed: {formatTime(video.updatedAt)} • Duration: {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '4px 12px', 
                    background: '#16a34a', 
                    color: '#fff', 
                    borderRadius: 6, 
                    fontSize: 12, 
                    fontWeight: 600 
                  }}>
                    Ready
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Resolutions: {video.resolutions.join(', ')}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace' }}>
                  ID: {video.id}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recently Failed */}
      {status?.recentlyFailed?.length > 0 && (
        <section>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Recently Failed</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {status.recentlyFailed.map(video => (
              <div key={video.id} style={{ 
                padding: 16, 
                background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', 
                borderRadius: 12,
                border: '1px solid #dc262633'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{video.title}</h3>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      Failed: {formatTime(video.updatedAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ 
                      padding: '4px 12px', 
                      background: '#dc2626', 
                      color: '#fff', 
                      borderRadius: 6, 
                      fontSize: 12, 
                      fontWeight: 600 
                    }}>
                      Failed
                    </div>
                    <button
                      onClick={() => deleteVideo(video.id)}
                      disabled={deleting === video.id}
                      style={{
                        padding: '6px 12px',
                        background: deleting === video.id ? '#999' : '#dc2626',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: deleting === video.id ? 'not-allowed' : 'pointer',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        if (deleting !== video.id) {
                          e.currentTarget.style.background = '#b91c1c'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (deleting !== video.id) {
                          e.currentTarget.style.background = '#dc2626'
                        }
                      }}
                    >
                      {deleting === video.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
                {video.processingError && (
                  <div style={{ 
                    marginTop: 8, 
                    padding: 12, 
                    background: '#dc26261a', 
                    borderRadius: 8, 
                    fontSize: 13, 
                    color: '#dc2626',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word'
                  }}>
                    {video.processingError}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace', marginTop: 8 }}>
                  ID: {video.id}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
